/**
 * FinanceListTab — a LISTA das abas "A receber" e "A pagar" de `/financeiro`.
 *
 * Ela é uma AGENDA: agrupa por dia de vencimento e carimba cada grupo com
 * "Vencido"/"Hoje"/"Em 3 dias". Essa estrutura é a razão de a ordenação da tela
 * só oferecer as duas direções da data (ver `financeListFilters`).
 *
 * ─── O RECORTE NÃO MORA MAIS AQUI ───────────────────────────────────────────
 *
 * Busca, status, período e faixa de valor foram para a PÁGINA quando ela ganhou
 * o hero com valor ao vivo: o número do hero é a soma do que esta lista mostra,
 * e duas implementações do mesmo recorte discordariam na primeira correção, sem
 * erro nenhum (#82/#69). Este componente recebe as linhas JÁ recortadas e
 * ordenadas e cuida do que é dele: agrupar, exibir e agir (baixa, edição,
 * exclusão com escopo de recorrência).
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle2, MoreVertical, Paperclip, Pencil, Trash2, Inbox } from 'lucide-react';
import { useMarkPaid, useDeleteFinanceEntry } from '@/hooks/useFinance';
import { MarkPaidDialog } from '@/components/finance/MarkPaidDialog';
import { todayIsoDate } from '@/lib/financeStatus';
import { effectiveFinanceStatus } from '@/lib/financeOverview';
import { dueBadge, formatBRL, STATUS_LABELS } from '@/lib/financeLabels';
import { localDateFromIsoDatePart } from '@/lib/calendarEvents';
import { CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import type { FinanceEntryWithStatus, FinanceKind } from '@/types/database';

interface Props {
  kind: FinanceKind;
  /** Linhas JÁ recortadas e ordenadas pela página. */
  entries: FinanceEntryWithStatus[];
  isLoading?: boolean;
  /** Há busca/status/filtro ativos — muda o texto do estado vazio. */
  recorteAtivo?: boolean;
  onEdit: (entry: FinanceEntryWithStatus) => void;
}

const toneClass: Record<string, string> = {
  overdue: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  today: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  soon: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  future: 'bg-muted text-muted-foreground',
};

export function FinanceListTab({ kind, entries, isLoading, recorteAtivo, onEdit }: Props) {
  const [toDelete, setToDelete] = useState<FinanceEntryWithStatus | null>(null);
  // Lançamento aguardando confirmação da data de baixa (ver MarkPaidDialog).
  const [toMarkPaid, setToMarkPaid] = useState<FinanceEntryWithStatus | null>(null);

  const markPaid = useMarkPaid();
  const deleteEntry = useDeleteFinanceEntry();

  /**
   * Agrupamento por dia de vencimento, PRESERVANDO a ordem que a página
   * escolheu: percorrer as linhas na ordem recebida e criar o grupo na primeira
   * aparição faz o grupo herdar a direção da ordenação (crescente ou
   * decrescente) sem reordenar nada aqui — reordenar seria uma segunda regra de
   * ordem, e ela discordaria do rótulo que a tela mostra.
   */
  const groups = useMemo(() => {
    const today = todayIsoDate();
    const map = new Map<string, FinanceEntryWithStatus[]>();
    for (const e of entries) {
      const arr = map.get(e.due_date) ?? [];
      arr.push(e);
      map.set(e.due_date, arr);
    }
    return { today, entries: Array.from(map.entries()) };
  }, [entries]);

  const isRecurring = (e: FinanceEntryWithStatus) =>
    Boolean(e.recurrence_parent_id) || (e.recurrence && e.recurrence !== 'none');

  const handleDelete = (scope: 'single' | 'future') => {
    if (!toDelete) return;
    deleteEntry.mutate({ id: toDelete.id, scope });
    setToDelete(null);
  };

  return (
    <div className="space-y-4">
      {isLoading && entries.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className={cn(CONTENT_CARD, 'h-20 animate-pulse')} />
          ))}
        </div>
      ) : groups.entries.length === 0 ? (
        <div
          className={cn(
            CONTENT_CARD,
            'flex flex-col items-center justify-center border-dashed py-8 text-center',
          )}
        >
          <Inbox className="mb-3 h-12 w-12 text-muted-foreground/50" />
          <p className="font-medium">
            {recorteAtivo ? 'Nenhum lançamento encontrado' : 'Nenhum lançamento'}
          </p>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            {recorteAtivo
              ? 'Nenhum lançamento corresponde à busca e aos filtros escolhidos.'
              : `Registre o que você tem ${kind === 'income' ? 'a receber' : 'a pagar'} para acompanhar o fluxo de caixa.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.entries.map(([date, dayEntries]) => {
            const badge = dueBadge(date, groups.today);
            return (
              <div key={date} className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <span className="text-sm font-semibold">
                    {format(localDateFromIsoDatePart(date), "dd 'de' MMM", { locale: ptBR })}
                  </span>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[11px] font-medium',
                      toneClass[badge.tone],
                    )}
                  >
                    {badge.label}
                  </span>
                </div>
                <div className="space-y-2">
                  {dayEntries.map((e) => {
                    // O status EFETIVO vem da view (`computed_status`); sem ela,
                    // a conta é refeita localmente. Ler `status` cru mostraria
                    // "Pendente" num título vencido (ver `financeOverview`).
                    const st = effectiveFinanceStatus(e, groups.today);
                    const settled = e.status === 'paid' || e.status === 'canceled';
                    return (
                      <div key={e.id} className={cn(CONTENT_CARD, 'flex items-center gap-3 p-4')}>
                        {/* Coluna da ESQUERDA: identificação + conteúdo. Ela é
                            quem dita a altura do card — o `items-center` do card
                            faz o bloco de ações centrar contra ELA, e não contra
                            a linha da descrição (#189). */}
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="secondary" className="h-5 px-1.5 py-0 text-[10px]">
                              {STATUS_LABELS[st]}
                            </Badge>
                            {/* Uma despesa cobrável também COBRA num orçamento;
                                sem a marca aqui, ela parece uma saída comum e o
                                usuário não entende por que não consegue apagá-la
                                num orçamento aprovado. */}
                            {e.billable && (
                              <Badge variant="outline" className="h-5 px-1.5 py-0 text-[10px]">
                                Cobrável
                              </Badge>
                            )}
                            {e.attachment_url && (
                              <Paperclip
                                className="h-3.5 w-3.5 text-muted-foreground"
                                aria-label="Com comprovante"
                              />
                            )}
                          </div>

                          {/* `min-w-0` não é decoração (#175): sem ele um flex
                              item nasce com `min-width: auto` e uma descrição
                              sem espaços estica a linha para fora da viewport. E
                              sem `truncate`: é a descrição que distingue duas
                              linhas da mesma lista. */}
                          <div className="min-w-0 space-y-0.5">
                            <p className="min-w-0 break-words font-bold">{e.description}</p>
                            <p
                              className={cn(
                                'text-sm font-semibold',
                                kind === 'income' ? 'text-emerald-600' : 'text-red-600',
                              )}
                            >
                              {formatBRL(Number(e.amount ?? 0))}
                            </p>
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-1">
                          {!settled && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              onClick={() => setToMarkPaid(e)}
                              disabled={markPaid.isPending}
                            >
                              <CheckCircle2 className="mr-1 h-4 w-4" />
                              {kind === 'income' ? 'Recebido' : 'Pago'}
                            </Button>
                          )}

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                aria-label={`Ações do lançamento ${e.description}`}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => onEdit(e)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => setToDelete(e)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Baixa com data explícita (pode ser retroativa) */}
      <MarkPaidDialog
        entry={toMarkPaid}
        onOpenChange={(o) => !o && setToMarkPaid(null)}
        submitting={markPaid.isPending}
        onConfirm={async (paidDate) => {
          if (!toMarkPaid) return;
          try {
            await markPaid.mutateAsync({ id: toMarkPaid.id, paidDate });
          } catch {
            // O toast de erro já vem da mutation.
            return;
          }
          setToMarkPaid(null);
        }}
      />

      {/* Delete confirmation (with recurrence scope) */}
      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lançamento?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && isRecurring(toDelete)
                ? 'Este lançamento faz parte de uma recorrência. O que deseja excluir?'
                : 'Esta ação não pode ser desfeita.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            {toDelete && isRecurring(toDelete) ? (
              <>
                <AlertDialogAction className="w-full" onClick={() => handleDelete('single')}>
                  Excluir apenas esta
                </AlertDialogAction>
                <AlertDialogAction
                  className="w-full bg-red-600 hover:bg-red-700"
                  onClick={() => handleDelete('future')}
                >
                  Excluir esta e as futuras
                </AlertDialogAction>
                <AlertDialogCancel className="mt-0 w-full">Cancelar</AlertDialogCancel>
              </>
            ) : (
              <>
                <AlertDialogAction
                  className="w-full bg-red-600 hover:bg-red-700"
                  onClick={() => handleDelete('single')}
                >
                  Excluir
                </AlertDialogAction>
                <AlertDialogCancel className="mt-0 w-full">Cancelar</AlertDialogCancel>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
